// Maps each Hebrew root letter to a stable color and an emoji "icon" mnemonic.
// Icons are based on the ancient pictographic meaning of each letter, so no
// image generation is required — the emoji itself is the memory hook.

// Alphabet order: aleph, bet, gimel, dalet, he, vav, zayin, chet, tet, yod,
// kaf, lamed, mem, nun, samekh, ayin, pe, tsadi, qof, resh, shin, tav.
const ROOT_LETTER_INFOS = [
  {letter: 'א', name: 'aleph', icon: '🐂', color: '#e74c3c', mnemonic: 'ox'},
  {letter: 'ב', name: 'bet', icon: '🏠', color: '#2980b9', mnemonic: 'house'},
  {letter: 'ג', name: 'gimel', icon: '🐪', color: '#16a085', mnemonic: 'camel'},
  {letter: 'ד', name: 'dalet', icon: '🚪', color: '#8e44ad', mnemonic: 'door'},
  {letter: 'ה', name: 'he', icon: '🪟', color: '#d35400', mnemonic: 'window'},
  {letter: 'ו', name: 'vav', icon: '🔩', color: '#27ae60', mnemonic: 'peg'},
  {letter: 'ז', name: 'zayin', icon: '⚔️', color: '#c0392b', mnemonic: 'weapon'},
  {letter: 'ח', name: 'chet', icon: '🧱', color: '#f39c12', mnemonic: 'wall'},
  {letter: 'ט', name: 'tet', icon: '🐍', color: '#0e6eb8', mnemonic: 'snake'},
  {letter: 'י', name: 'yod', icon: '✋', color: '#9b59b6', mnemonic: 'hand'},
  {letter: 'כ', name: 'kaf', icon: '🖐️', color: '#1abc9c', mnemonic: 'palm'},
  {letter: 'ל', name: 'lamed', icon: '📚', color: '#2c3e50', mnemonic: 'learn'},
  {letter: 'מ', name: 'mem', icon: '💧', color: '#3498db', mnemonic: 'water'},
  {letter: 'נ', name: 'nun', icon: '🐟', color: '#e67e22', mnemonic: 'fish'},
  {letter: 'ס', name: 'samekh', icon: '🕸️', color: '#7f8c8d', mnemonic: 'support'},
  {letter: 'ע', name: 'ayin', icon: '👁️', color: '#ad1457', mnemonic: 'eye'},
  {letter: 'פ', name: 'pe', icon: '👄', color: '#5b2c6f', mnemonic: 'mouth'},
  {letter: 'צ', name: 'tsadi', icon: '🎣', color: '#138d75', mnemonic: 'fishhook'},
  {letter: 'ק', name: 'qof', icon: '🐒', color: '#b9770e', mnemonic: 'monkey'},
  {letter: 'ר', name: 'resh', icon: '🧠', color: '#6b3fa0', mnemonic: 'head'},
  {letter: 'ש', name: 'shin', icon: '🦷', color: '#1f618d', mnemonic: 'tooth'},
  {letter: 'ת', name: 'tav', icon: '✅', color: '#117a65', mnemonic: 'mark'},
];

const INFO_BY_LETTER = ROOT_LETTER_INFOS.reduce((acc, info) => {
  acc[info.letter] = info;
  return acc;
}, {});

// Final (sofit) forms collapse onto their base letter.
const SOFIT_TO_BASE = {
  'ך': 'כ',
  'ם': 'מ',
  'ן': 'נ',
  'ף': 'פ',
  'ץ': 'צ',
};

const FALLBACK_COLOR = '#6c5ce7';
const FALLBACK_ICON = '🔤';

export function normalizeRootLetter(letter = '') {
  const ch = String(letter).trim();
  return SOFIT_TO_BASE[ch] || ch;
}

// Split a stored root string such as "כ - ת - ב" into its individual letters.
export function parseRootLetters(rootStr = '') {
  return String(rootStr || '')
    .split(/\s*-\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function getRootLetterInfo(letter = '') {
  const base = normalizeRootLetter(letter);
  const info = INFO_BY_LETTER[base];
  if (info) return info;
  return {
    letter: base,
    name: base.toLowerCase(),
    icon: FALLBACK_ICON,
    color: FALLBACK_COLOR,
    mnemonic: '',
  };
}

export function getRootLetterColor(letter = '') {
  return getRootLetterInfo(letter).color;
}

export function getRootLetterIcon(letter = '') {
  return getRootLetterInfo(letter).icon;
}

// Build an icon-based mnemonic sentence for a root, always available (no AI).
export function buildRootMemoryMnemonic(rootLetters = [], meaning = '') {
  if (!rootLetters || rootLetters.length === 0) return '';
  const parts = rootLetters.map((l) => {
    const info = getRootLetterInfo(l);
    return `${info.icon} ${info.name} (${info.mnemonic})`;
  });
  const chain = parts.join(' → ');
  const tail = meaning ? ` — together they spell "${meaning}".` : '.';
  return `Root memory: ${chain}${tail} Picture each icon in order to recall the letters.`;
}

// --- Root-letter highlighting inside Hebrew text ----------------------------

// Hebrew combining marks (niqqud / dagesh / cantillation) attach to a base
// letter. We group each base letter with its trailing marks so coloring never
// separates a letter from its vowels.
const HEBREW_BASE = /[\u05D0-\u05EA]/;
const HEBREW_MARK = /[\u0591-\u05C7]/;

function isHebrewBase(ch) {
  return HEBREW_BASE.test(ch);
}
function isHebrewMark(ch) {
  return HEBREW_MARK.test(ch);
}

// Split Hebrew text into clusters: [{ chars: [..], base: 'כ' }, ...].
// Non-Hebrew characters (spaces, latin, geresh) become single-char clusters
// with base === '' so they are never colored.
function buildClusters(text) {
  const clusters = [];
  for (const ch of text) {
    if (isHebrewBase(ch)) {
      clusters.push({chars: [ch], base: normalizeRootLetter(ch)});
    } else if (isHebrewMark(ch) && clusters.length > 0) {
      clusters[clusters.length - 1].chars.push(ch);
    } else {
      clusters.push({chars: [ch], base: ''});
    }
  }
  return clusters;
}

// Returns an array of { text, color } segments. Each root letter (in order)
// is matched to its first unused occurrence in the text and given that
// letter's color; everything else stays uncolored. This is a learning aid,
// not morphological analysis — weak/guttural roots may leave a letter uncolored.
export function segmentHebrewByRoot(text = '', rootLetters = []) {
  if (!text) return [];
  const clusters = buildClusters(text);
  if (!rootLetters || rootLetters.length < 2) {
    return [{text, color: null}];
  }

  const used = new Array(clusters.length).fill(false);

  // Match each root letter (in root order) to the next matching base letter.
  rootLetters.forEach((letter) => {
    const base = normalizeRootLetter(letter);
    const color = getRootLetterColor(letter);
    for (let i = 0; i < clusters.length; i++) {
      if (used[i]) continue;
      if (clusters[i].base === base) {
        clusters[i].color = color;
        used[i] = true;
        break;
      }
    }
  });

  return clusters.map((c) => ({
    text: c.chars.join(''),
    color: c.color || null,
  }));
}
